import React, { useEffect, useState } from 'react';
import { History, Plus, Loader2, LayoutGrid } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import StepIndicator from './components/StepIndicator';
import RunHistoryList from './components/RunHistoryList';
import ProxyInitStep from './steps/ProxyInitStep';
import InputSelectionStep from './steps/InputSelectionStep';
import ResultStep from './steps/ResultStep';
import { calculateScore, getRun, proxyInit } from './api';

/**
 * JPRO Profitability Index — 5-step Chain of Custody.
 *   1. Proxy Init       → ZIP + program fetches baseline
 *   2. Input Selection  → per-variable upload or self-select
 *   3. Review + Logic Gate runs during the calculate call
 *   4. Result           → scores, velocity plan, save
 */
export default function ProfitabilityIndex() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [step, setStep] = useState('proxy');
    const [baseline, setBaseline] = useState(null);
    const [marketSnapshot, setMarketSnapshot] = useState(null);
    const [property, setProperty] = useState({ property_id: '', address: '', zip_code: '', programs: ['LIHTC'], buildings: [] });
    const [inputs, setInputs] = useState({ profitability: {}, affordability: {} });
    const [uploadedDocuments, setUploadedDocuments] = useState([]);
    // Each entry: { filename, size_kb, document_type, inferred_label, reasoning,
    //               source, contributed_variables: [keys], contributed_buildings: [names] }
    const [scoreResponse, setScoreResponse] = useState(null);
    const [showHistory, setShowHistory] = useState(false);

    const recordUpload = (entry) => setUploadedDocuments(prev => [...prev, entry]);

    // Mutation for the "Run again" baseline refresh — same shape as the
    // initial proxy fetch in ProxyInitStep, but lives here because runAgain
    // is initiated from ResultStep via callback.
    const refreshBaselineMutation = useMutation({
        mutationFn: async ({ zip, programs, address }) => {
            const res = await proxyInit({ zip_code: zip, programs, address });
            if (!res.ok) {
                let detail = 'Baseline refresh failed';
                try { detail = (await res.json()).detail || detail; } catch (_) {}
                throw new Error(detail);
            }
            return await res.json();
        },
        onSuccess: (data) => {
            setBaseline(data);
            setMarketSnapshot(data?.market_snapshot || null);
        },
    });
    const refreshingBaseline = refreshBaselineMutation.isPending;

    // Auto-open a saved run when the URL carries ?run=<id> (e.g., from the
    // Portfolio table). Strips the param after rehydration so a back-button
    // press doesn't keep firing the fetch.
    useEffect(() => {
        const runId = searchParams.get('run');
        if (!runId) return;
        openRun(runId).finally(() => {
            const next = new URLSearchParams(searchParams);
            next.delete('run');
            setSearchParams(next, { replace: true });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const { mutate: runCalculate, isPending, error } = useMutation({
        mutationFn: async () => {
            const body = {
                property,
                profitability: inputs.profitability,
                affordability: inputs.affordability,
            };
            const res = await calculateScore(body);
            if (!res.ok) throw new Error('Calculate failed');
            return res.json();
        },
        onSuccess: (data) => {
            setScoreResponse(data);
            setStep('result');
        },
    });

    const openRun = async (id) => {
        const res = await getRun(id);
        if (!res.ok) return null;
        const { run } = await res.json();
        // Pull the full property snapshot from saved inputs (includes units,
        // buildings, placed_in_service, etc. — backend Pydantic rejects the
        // velocity_plan request without `units`, hence the 422.)
        const savedProperty = run.inputs?.property || {};
        setProperty({
            property_id: savedProperty.property_id || run.property_id || run.property_name || '',
            address: savedProperty.address || run.address || '',
            zip_code: savedProperty.zip_code || run.zip_code || '',
            programs: savedProperty.programs || run.programs || (run.program_type ? [run.program_type] : []),
            units: savedProperty.units || 1,
            buildings_count: savedProperty.buildings_count,
            buildings: savedProperty.buildings || [],
            placed_in_service: savedProperty.placed_in_service,
            overall_condition: savedProperty.overall_condition,
        });
        setInputs({
            profitability: run.inputs?.profitability || {},
            affordability: run.inputs?.affordability || {},
        });
        setUploadedDocuments(run.inputs?.uploaded_documents || []);
        // Rehydrate the full score response shape so ResultStep can render from
        // storage without recalling model_service.
        setScoreResponse({
            scores: run.scores,
            impacts: run.scores?.impacts || [],
            critical_override: run.scores?.critical_override || { triggered: false, reasons: [] },
            summary_card: run.scores?.summary_card || {},
            _restored: { narrative: run.narrative, velocity_plan: run.velocity_plan, source_run_id: id },
        });
        setStep('result');
        setShowHistory(false);
        return run;
    };

    // Re-evaluate the same property with fresh market data — KEEP the evidence
    // (variables, uploaded documents, buildings). Only the score and the
    // baseline get cleared so the user gets a re-score.
    //
    // Flow:
    //   1. Clear scoreResponse + baseline; flip to step 'inputs'
    //   2. Trigger baseline refresh (mutation)
    //   3. When baseline returns, smart-merge proxy variables (proxy ratings
    //      replaced with fresh values; doc-verified/self-selected variables
    //      kept; proxy raw fills gaps in user raws)
    //   4. Auto-advance to step 'review' and run Calculate. The user
    //      explicitly chose "Run again" — they want a re-score, not a tour
    //      of the inputs page.
    //
    // For a clean slate, the user should click "Start over" instead.
    const runAgain = async () => {
        setScoreResponse(null);
        setBaseline(null);
        setMarketSnapshot(null);
        setStep('inputs');

        if (!property.zip_code || !property.programs?.length) return;

        try {
            await refreshBaselineMutation.mutateAsync({
                zip: property.zip_code,
                programs: property.programs,
                address: property.address,
            });
            // Baseline arrived; evidence preserved; advance to scoring.
            setStep('review');
            runCalculate();
        } catch (e) {
            // Baseline refresh failed — leave the user on Step 2 with their
            // evidence intact so they can manually recalculate or correct.
        }
    };

    const startOver = () => {
        setStep('proxy');
        setBaseline(null);
        setProperty({ property_id: '', address: '', zip_code: '', programs: ['LIHTC'], buildings: [] });
        setInputs({ profitability: {}, affordability: {} });
        setUploadedDocuments([]);
        setScoreResponse(null);
    };

    return (
        <div className="max-w-6xl mx-auto p-6">
            <header className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Profitability Index</h1>
                    <p className="text-sm text-slate-600 dark:text-slate-400">The intersection of profitability and affordability — measured, tiered, actionable.</p>
                </div>
                <div className="flex items-center gap-2">
                    <Link to="/dashboard/profitability-index/portfolio"
                          className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-slate-300 dark:border-slate-700 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                        <LayoutGrid size={16} /> Portfolio
                    </Link>
                    <button onClick={() => setShowHistory(true)} className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-slate-300 dark:border-slate-700 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                        <History size={16} /> History
                    </button>
                    <button onClick={startOver} className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm shadow-sm transition-colors">
                        <Plus size={16} /> New Run
                    </button>
                </div>
            </header>

            <StepIndicator currentStep={step} />

            {error && (
                <div className="mb-4 p-3 rounded-md bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 text-sm text-red-700 dark:text-red-200">
                    {error.message}
                </div>
            )}

            {step === 'proxy' && (
                <ProxyInitStep
                    initial={property}
                    onComplete={({ zip_code, programs, address, baseline: b }) => {
                        setProperty(p => ({ ...p, zip_code, programs, address }));
                        setBaseline(b);
                        setMarketSnapshot(b?.market_snapshot || null);
                        setStep('inputs');
                    }}
                />
            )}

            {step === 'inputs' && (
                <InputSelectionStep
                    baseline={baseline}
                    marketSnapshot={marketSnapshot}
                    onChangeMarketSnapshot={setMarketSnapshot}
                    refreshingBaseline={refreshingBaseline}
                    property={property}
                    onChangeProperty={setProperty}
                    inputs={inputs}
                    onChangeInputs={setInputs}
                    onRecordUpload={recordUpload}
                    onBack={() => setStep('proxy')}
                    onNext={() => { setStep('review'); runCalculate(); }}
                />
            )}

            {step === 'review' && (
                <div className="flex items-center justify-center p-10 text-slate-600 dark:text-slate-300">
                    <Loader2 className="animate-spin mr-2" size={18} />
                    Running logic gate and calculating score…
                </div>
            )}

            {step === 'result' && scoreResponse && (
                <ResultStep
                    property={property}
                    inputs={inputs}
                    uploadedDocuments={uploadedDocuments}
                    scoreResponse={scoreResponse}
                    isRestoredRun={!!scoreResponse._restored?.source_run_id}
                    onBack={() => setStep('inputs')}
                    onStartOver={startOver}
                    onRunAgain={runAgain}
                />
            )}

            {showHistory && (
                <div className="fixed inset-0 bg-black/30 flex items-start justify-center pt-20 z-50" onClick={() => setShowHistory(false)}>
                    <div className="bg-white dark:bg-slate-900 rounded-lg shadow-xl max-w-2xl w-full max-h-[75vh] overflow-y-auto p-5 mx-4" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Saved Runs</h2>
                            <button onClick={() => setShowHistory(false)} className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200">Close</button>
                        </div>
                        <RunHistoryList onOpen={openRun} />
                    </div>
                </div>
            )}
        </div>
    );
}
